/**
 * Helpers extracted from the `onSearch` store action and the `handleKeyDown`
 * (Enter-to-search) effect in SearchResults/index.jsx
 * (TD-2377 — SonarQube cognitive-complexity cleanup).
 *
 * Both `onSearch` and `handleKeyDown` walked the SAME nested
 * (searchType × searchCategory) decision matrix — one to fire the search, the
 * other to decide whether Enter is allowed to. That duplicated tree is what
 * pushed both functions well over SonarQube's cognitive-complexity threshold of
 * 15 (38 and 45 respectively). The matrix now lives here as a flat list of
 * descriptors keyed by (searchType, searchCategory); `resolveSearchDescriptor`
 * turns the current view `state` into the single matching descriptor (or null),
 * collapsing both call sites to a linear body.
 *
 * A descriptor names the `state` properties for that combination via string
 * keys (read with bracket access) rather than closing over the store, so the
 * resolution logic is a pure function of a plain `state` object and directly
 * unit-testable — the SearchResults store has no unit harness. Mirrors the
 * sibling `getResultsHelpers.js` precedent from Phase 1 of this ticket.
 */
import { UI_OPTIONS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import {
  SEARCH_FIELDS,
  FUND_SEARCH_CATEGORY_FIELDS,
  PORTFOLIO_SEARCH_CATEGORY_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS
} from '../../../utils/constants/fields';

const SEARCH_TYPE = UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE];
const FUND_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY];
const PORTFOLIO_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY];
const PROFILER_CATEGORY = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY];

/**
 * One entry per supported (searchType × searchCategory) combination. String
 * `*Key` fields index into the view `state`; `setErrorStateAction` is the name
 * of the store action that flips the submit-error flag named by `errorStateKey`.
 * `resetSort` mirrors the Funds/Portfolio branches that reset the sort before
 * navigating. Order is irrelevant — the (type, category) pairs are disjoint.
 */
export const SEARCH_DISPATCH = [
  {
    searchType: SEARCH_TYPE.Profiler,
    searchCategory: PROFILER_CATEGORY['Characteristics'],
    categoryFieldsKey: 'profilerSearchCategoryFields',
    categoryFieldName: PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME,
    criteriaFieldsKey: 'profilerCompanyCriteriaFields',
    validationFieldsKey: 'profilerCompanyCriteriaValidationFields',
    errorStateKey: 'onProfilerCompanyCriteriaSubmitErrorState',
    setErrorStateAction: 'setOnProfilerCompanyCriteriaSubmitErrorState',
    searchDisabledKey: 'isProfilerCompanyCriteriaSearchDisabled',
    resetSort: false
  },
  {
    searchType: SEARCH_TYPE.Profiler,
    searchCategory: PROFILER_CATEGORY['Industries'],
    categoryFieldsKey: 'profilerSearchCategoryFields',
    categoryFieldName: PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME,
    criteriaFieldsKey: 'profilerIndustryCodesFields',
    validationFieldsKey: 'profilerIndustryCodesValidationFields',
    errorStateKey: 'onProfilerIndustryCodesSubmitErrorState',
    setErrorStateAction: 'setOnProfilerIndustryCodesSubmitErrorState',
    searchDisabledKey: 'isProfilerIndustryCodesSearchDisabled',
    resetSort: false
  },
  {
    searchType: SEARCH_TYPE.Funds,
    searchCategory: FUND_CATEGORY['Investment criteria'],
    categoryFieldsKey: 'fundsSearchCategoryFields',
    categoryFieldName: FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME,
    criteriaFieldsKey: 'fundsInvestmentCriteriaFields',
    validationFieldsKey: 'fundsInvestmentCriteriaValidationFields',
    errorStateKey: 'onFundsInvestmentCriteriaSubmitErrorState',
    setErrorStateAction: 'setOnFundsInvestmentCriteriaSubmitErrorState',
    searchDisabledKey: 'isFundsInvestmentCriteriaSearchDisabled',
    resetSort: true
  },
  {
    searchType: SEARCH_TYPE['Portfolio Co.'],
    searchCategory: PORTFOLIO_CATEGORY['Investment criteria'],
    categoryFieldsKey: 'portfoliosSearchCategoryFields',
    categoryFieldName: PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME,
    criteriaFieldsKey: 'portfoliosInvestmentCriteriaFields',
    validationFieldsKey: 'portfoliosInvestmentCriteriaValidationFields',
    errorStateKey: 'onPortfoliosInvestmentCriteriaSubmitErrorState',
    setErrorStateAction: 'setOnPortfoliosInvestmentCriteriaSubmitErrorState',
    searchDisabledKey: 'isPortfoliosInvestmentCriteriaSearchDisabled',
    resetSort: true
  }
];

// The active search type is stored on the funds-criteria field bag (it is the
// shared top-level selector both call sites read).
export const getSearchType = (state) =>
  state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.SEARCH_TYPE.NAME][0];

// The active search category for a given descriptor's category field bag.
const getSearchCategory = (state, descriptor) =>
  state[descriptor.categoryFieldsKey][descriptor.categoryFieldName][0];

/**
 * Resolve the current (searchType × searchCategory) to its dispatch descriptor,
 * or null when the combination has no search action wired up (e.g. a category
 * that isn't "Investment criteria"/"Characteristics"/"Industries"). Pure.
 */
export const resolveSearchDescriptor = (state) => {
  const searchType = getSearchType(state);
  return (
    SEARCH_DISPATCH.find(
      (descriptor) =>
        descriptor.searchType === searchType &&
        getSearchCategory(state, descriptor) === descriptor.searchCategory
    ) ?? null
  );
};

// Serialise a criteria field bag into the `key=<uri-encoded JSON>` query string
// the search URL expects. Extracted verbatim from the four onSearch branches.
export const buildFieldsParams = (fields) =>
  Object.entries(fields)
    .map(([key, value]) => `${[key]}=${encodeURIComponent(JSON.stringify(value))}`)
    .join('&');

/**
 * Whether pressing Enter should fire the search for the current state: there
 * must be a wired-up descriptor and its search must not be disabled. Collapses
 * the `handleKeyDown` matrix + per-branch `is*SearchDisabled` guard.
 */
export const canExecuteSearchOnEnter = (state) => {
  const descriptor = resolveSearchDescriptor(state);
  return !!descriptor && !state[descriptor.searchDisabledKey];
};
